// Shared UI building blocks.
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { TRUCK_TYPES, TRAILER_TYPES, isTrailerType } from '../shared/calc';
import { api, navigate, statusTone, uploadPhoto, photoSrc, useApi, DEMO, readPhoto, cleanPasted, type Row } from './lib';

// ─── Icons (stroke icons, 24×24) ───
const P: Record<string, string> = {
  home: 'M3 10.5 12 3l9 7.5V21h-6v-6H9v6H3z',
  clipboard: 'M9 4h6v3H9zM7 5H5v16h14V5h-2M9 12h6M9 16h4',
  invoice: 'M6 2h9l5 5v15H6zM14 2v6h6M9 13h7M9 17h5',
  users: 'M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 20v-2a4 4 0 0 0-3-3.9M16 2.1a4 4 0 0 1 0 7.8',
  truck: 'M1 4h13v12H1zM14 8h4l4 4v4h-8M5.5 19.5a2 2 0 1 0 0-.01M18.5 19.5a2 2 0 1 0 0-.01',
  box: 'M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8',
  wrench: 'M14.7 6.3a4 4 0 0 0 5 5L22 14l-8 8-2.3-2.3a4 4 0 0 0-5-5L2 10l8-8z',
  chart: 'M3 3v18h18M7 15l4-4 3 3 5-6',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2',
  camera: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  plus: 'M12 5v14M5 12h14',
  bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3',
  x: 'M18 6 6 18M6 6l12 12',
  check: 'M20 6 9 17l-5-5',
  upload: 'M12 16V4M7 9l5-5 5 5M4 20h16',
  grid: 'M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  alert: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
  back: 'M15 18l-6-6 6-6',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  printer: 'M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  menu: 'M3 12h18M3 6h18M3 18h18',
  maximize: 'M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3',
  board: 'M3 3h7v18H3zM14 3h7v10h-7zM14 17h7v4h-7z',
  camera2: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4z',
  dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  phone: 'M5 2h14v20H5zM11 18h2',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  mail: 'M4 4h16v16H4zM22 6l-10 7L2 6',
  link: 'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
  image: 'M3 3h18v18H3zM8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M21 15l-5-5L5 21',
  refresh: 'M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15',
};
export function Icon({ name, size = 18, className = '' }: { name: keyof typeof P | string; size?: number; className?: string }) {
  return (
    <svg className={'icon ' + className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={P[name] || ''} />
    </svg>
  );
}

// ─── Toasts ───
const ToastCtx = createContext<(msg: string, tone?: 'ok' | 'err') => void>(() => {});
export const useToast = () => useContext(ToastCtx);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [t, setT] = useState<{ msg: string; tone: string; id: number } | null>(null);
  useEffect(() => { if (!t) return; const h = setTimeout(() => setT(null), t.tone === 'err' ? 6000 : 3000); return () => clearTimeout(h); }, [t]);
  // "Moved to trash — Undo" after any delete / void.
  const [undo, setUndo] = useState<{ id: string; busy?: boolean } | null>(null);
  useEffect(() => { const on = (e: Event) => setUndo({ id: (e as CustomEvent<string>).detail }); window.addEventListener('turan:trashed', on); return () => window.removeEventListener('turan:trashed', on); }, []);
  useEffect(() => { if (!undo || undo.busy) return; const h = setTimeout(() => setUndo(null), 8000); return () => clearTimeout(h); }, [undo]);
  const doUndo = async () => {
    if (!undo) return;
    setUndo({ ...undo, busy: true });
    try {
      const r = await fetch(`/api/trash/${undo.id}/restore`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not undo');
      setUndo(null); location.reload();
    } catch (e) { setUndo(null); setT({ msg: (e as Error).message, tone: 'err', id: Date.now() }); }
  };
  return (
    <ToastCtx.Provider value={(msg, tone = 'ok') => setT({ msg, tone, id: Date.now() })}>
      {children}
      {t && <div className={'toast ' + t.tone + (undo ? ' raised' : '')} role="status" key={t.id}><Icon name={t.tone === 'err' ? 'alert' : 'check'} />{t.msg}</div>}
      {undo && <div className="toast undo-bar" role="status"><Icon name="trash" />Moved to the trash<button type="button" disabled={undo.busy} onClick={() => void doUndo()}>{undo.busy ? 'Undoing…' : 'Undo'}</button></div>}
    </ToastCtx.Provider>
  );
}

// ─── Basic pieces ───
export const Badge = ({ children, tone }: { children: ReactNode; tone?: string }) =>
  <span className={'badge ' + (tone || statusTone(String(children)))}>{children}</span>;

export function Button({ children, kind = 'secondary', icon, busy, className = '', ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: 'primary' | 'secondary' | 'danger' | 'ghost'; icon?: string; busy?: boolean }) {
  return (
    <button type="button" {...rest} className={`btn ${kind} ${className}`} disabled={busy || rest.disabled}>
      {icon && <Icon name={icon} size={16} />}{busy ? 'Saving…' : children}
    </button>
  );
}

export function Link({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return <a href={to} className={className} onClick={e => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(to); }}>{children}</a>;
}

export const Stat = ({ label, value, hint, tone, onClick }: { label: string; value: ReactNode; hint?: ReactNode; tone?: string; onClick?: () => void }) => (
  <div className={'stat ' + (tone || '') + (onClick ? ' clickable' : '')} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
    <span>{label}</span><strong>{value}</strong>{hint && <small>{hint}</small>}
  </div>
);

export const Panel = ({ title, action, children, className = '' }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) => (
  <section className={'panel ' + className}>
    {(title || action) && <div className="panel-head"><h3>{title}</h3>{action}</div>}
    {children}
  </section>
);

export const Empty = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>;
export const Loading = () => <div className="loading"><span className="spinner" />Loading…</div>;
export const ErrorBox = ({ error, onRetry }: { error: string; onRetry?: () => void }) =>
  error ? <div className="error-box" role="alert"><Icon name="alert" />{error}{onRetry && <button className="link" onClick={onRetry}>Retry</button>}</div> : null;

export function Table({ heads, rows, onRow, empty = 'Nothing here yet.', className = '', rowClass }: {
  heads: ReactNode[]; rows: ReactNode[][]; onRow?: (i: number) => void; empty?: ReactNode; className?: string; rowClass?: (i: number) => string;
}) {
  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <div className={'table-wrap ' + className}>
      <table>
        <thead><tr>{heads.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} onClick={onRow ? () => onRow(i) : undefined} className={(onRow ? 'clickable ' : '') + (rowClass ? rowClass(i) : '')}
              tabIndex={onRow ? 0 : undefined} onKeyDown={onRow ? e => e.key === 'Enter' && onRow(i) : undefined}>
              {r.map((c, j) => <td key={j} data-label={typeof heads[j] === 'string' ? (heads[j] as string) : ''}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Forms ───
export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // With a modal on top of another, Escape closes only the top one.
    const k = (e: KeyboardEvent) => { if (e.key !== 'Escape') return; const all = document.querySelectorAll('.overlay'); if (all[all.length - 1] === ref.current) onClose(); };
    window.addEventListener('keydown', k);
    document.body.classList.add('no-scroll');
    return () => { window.removeEventListener('keydown', k); setTimeout(() => { if (!document.querySelector('.overlay')) document.body.classList.remove('no-scroll'); }, 0); };
  }, [onClose]);
  return (
    // Clicking outside doesn't close it — a stray click shouldn't throw away what you typed. Use ✕, Cancel or Esc.
    <div className="overlay" ref={ref}>
      <div className={'modal' + (wide ? ' wide' : '')} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head"><h2>{title}</h2><button className="icon-btn" onClick={onClose} aria-label="Close"><Icon name="x" /></button></div>
        {children}
      </div>
    </div>
  );
}

/**
 * Form wrapper: collects named inputs, calls onSubmit, shows errors, disables while saving.
 */
export function Form({ onSubmit, children, submitLabel = 'Save', onCancel, danger, extraSubmit }: {
  onSubmit: (v: Record<string, string>, form: HTMLFormElement) => Promise<unknown>; children: ReactNode; submitLabel?: string; onCancel?: () => void; danger?: ReactNode;
  /** A second save button (e.g. "Save & New"): the values get _action = its value. */
  extraSubmit?: { label: string; value: string };
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = e.currentTarget;
    const values = Object.fromEntries([...new FormData(f).entries()].map(([k, val]) => [k, String(val)]));
    const by = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
    if (by?.name === '_action') values._action = by.value;
    setBusy(true); setError('');
    try { await onSubmit(values, f); } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="form">
      <div className="form-body">{children}</div>
      {error && <div className="error-box" role="alert"><Icon name="alert" />{error}</div>}
      <div className="form-foot">
        {danger}
        <span className="spacer" />
        {onCancel && <Button type="button" onClick={onCancel} disabled={busy}>Cancel</Button>}
        {extraSubmit && <button type="submit" name="_action" value={extraSubmit.value} className="btn secondary" disabled={busy}>{extraSubmit.label}</button>}
        <Button kind="primary" type="submit" busy={busy}>{submitLabel}</Button>
      </div>
    </form>
  );
}

type FieldProps = { label: string; name: string; hint?: ReactNode; full?: boolean } & React.InputHTMLAttributes<HTMLInputElement>;
export function Field({ label, hint, full, ...input }: FieldProps) {
  return <label className={'field' + (full ? ' full' : '')}><span>{label}{input.required && ' *'}</span><input {...input} />{hint && <small>{hint}</small>}</label>;
}
export function MoneyField({ label, hint, full, ...input }: FieldProps) {
  return (
    <label className={'field' + (full ? ' full' : '')}><span>{label}{input.required && ' *'}</span>
      <div className="affix"><em>$</em><input inputMode="decimal" type="number" step="0.01" min="0" {...input} /></div>{hint && <small>{hint}</small>}
    </label>
  );
}
/** Markup at a glance: reads the sell price and cost fields of the form it sits in — "Markup 75% · margin 43% · $16.52 profit each". */
export function markupText(price: number, cost: number) {
  if (!(price > 0) || !(cost > 0)) return '';
  const mk = Math.round(((price - cost) / cost) * 100), mg = Math.round(((price - cost) / price) * 100);
  return `Markup ${mk}% over cost · margin ${mg}% · ${(price - cost) < 0 ? '−' : ''}$${Math.abs(price - cost).toFixed(2)} ${price - cost < 0 ? 'loss' : 'profit'} each`;
}
export function MarkupHint({ price = 'unit_price', cost = 'unit_cost' }: { price?: string; cost?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [txt, setTxt] = useState('');
  useEffect(() => {
    const form = ref.current?.closest('form');
    if (!form) return;
    const read = () => {
      const p = Number((form.querySelector<HTMLInputElement>(`[name="${price}"]`)?.value || '').replace(/[^\d.]/g, ''));
      const c = Number((form.querySelector<HTMLInputElement>(`[name="${cost}"]`)?.value || '').replace(/[^\d.]/g, ''));
      setTxt(markupText(p, c));
    };
    read(); form.addEventListener('input', read);
    return () => form.removeEventListener('input', read);
  }, [price, cost]);
  return <span ref={ref} className={'markup-hint full' + (txt.includes('loss') ? ' loss' : '')}>{txt}</span>;
}
export function Select({ label, name, children, full, hint, ...rest }: { label: string; name: string; children: ReactNode; full?: boolean; hint?: ReactNode } & React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <label className={'field' + (full ? ' full' : '')}><span>{label}{rest.required && ' *'}</span><select name={name} {...rest}>{children}</select>{hint && <small>{hint}</small>}</label>;
}
export function TextArea({ label, name, full = true, ...rest }: { label: string; name: string; full?: boolean } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <label className={'field' + (full ? ' full' : '')}><span>{label}{rest.required && ' *'}</span><textarea name={name} rows={3} {...rest} /></label>;
}

// ─── Photos ───
export function PhotoGrid({ photos, onOpen }: { photos: Row[]; onOpen?: (p: Row) => void }) {
  const [view, setView] = useState<Row | null>(null);
  if (!photos.length) return null;
  return (
    <>
      <div className="photo-grid">
        {photos.map(p => (
          <button key={p.id} className="photo" onClick={() => (onOpen ? onOpen(p) : setView(p))} title={p.note || ''}>
            <img src={photoSrc(p.id)} alt={p.note || 'Photo'} loading="lazy" />
            {p.kind !== 'job' && <span className={'photo-tag ' + (p.review_status === 'open' ? 'open' : '')}>{({ paper_wo: 'Paper WO', part_used: p.review_status === 'open' ? 'Part · review' : 'Part ✓', receipt: 'Receipt' } as Row)[p.kind]}</span>}
          </button>
        ))}
      </div>
      {view && (
        <div className="overlay lightbox" onClick={() => setView(null)}>
          <img src={photoSrc(view.id)} alt="" />
          <div className="lightbox-cap">{view.note || ''} <small>{view.uploaded_by_name} · {new Date(view.created_at).toLocaleString()}</small></div>
        </div>
      )}
    </>
  );
}

/**
 * Truck / trailer fields with VIN lookup: type the 17-character VIN and year, make, model (and engine) fill in.
 * Field names get `prefix` (e.g. "nu_vin") so they can sit inside a bigger form.
 */
/** VIN lookup says "Truck"/"Trailer" (or a specific kind): keep what's chosen if it's the same kind, else a sensible default. */
function kindFrom(found: string | undefined, current: string) {
  if (!found) return current;
  if (found === 'Truck') return current && !isTrailerType(current) && current !== 'Other' ? current : 'Sleeper truck';
  if (found === 'Trailer') return isTrailerType(current) && current !== 'Trailer' ? current : 'Dry van';
  return found;
}

export function UnitFields({ prefix = '', initial, onExisting, unitRequired = true, vinRequired, only, customerId }: {
  prefix?: string; initial?: Row; onExisting?: (e: Row) => void; unitRequired?: boolean; vinRequired?: boolean; only?: 'truck' | 'trailer'; customerId?: string;
}) {
  const [f, setF] = useState({ vin: initial?.vin || '', unit: initial?.unit || '', type: initial?.type || '', year: initial?.year || '', make: initial?.make || '', model: initial?.model || '', plate: initial?.plate || '' });
  const [info, setInfo] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const last = useRef(initial?.vin || '');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF(x => ({ ...x, [k]: k === 'vin' ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17) : e.target.value }));
  async function lookup(vin: string) {
    last.current = vin; setBusy(true);
    try {
      const r = await api<Row>(`/api/vin/${vin}`);
      if (last.current !== vin) return;
      setInfo(r);
      setF(x => ({ ...x, year: r.year || x.year, make: r.make || x.make, model: r.model || x.model, type: kindFrom(r.type, x.type), unit: x.unit || vin.slice(-6) }));
    } catch (e) { setInfo({ message: (e as Error).message }); } finally { setBusy(false); }
  }
  useEffect(() => { if (f.vin.length === 17 && f.vin !== last.current) void lookup(f.vin); if (f.vin.length < 17) setInfo(null); }, [f.vin]);
  const others = ((info?.existing || []) as Row[]).filter(e => e.id !== initial?.id);
  return (
    <>
      <label className="field full vin-field"><span>VIN{vinRequired && ' *'}</span>
        <div className="affix right"><input name={prefix + 'vin'} value={f.vin} onChange={set('vin')} required={vinRequired} minLength={vinRequired ? 17 : undefined} placeholder="17 characters — fills year, make, model" autoCapitalize="characters" autoComplete="off" spellCheck={false} inputMode="text" />
          <em>{busy ? '…' : `${f.vin.length}/17`}</em></div>
        <div className="row" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <PhotoRead kind="vin" small onResult={r => { if (r.vin) setF(x => ({ ...x, vin: String(r.vin).slice(0, 17) })); }} />
          {canScan() && <ScanButton label="Scan VIN barcode" photoKind="vin" onCode={code => { const v = code.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/^I/, ''); setF(x => ({ ...x, vin: v.slice(-17) })); }} />}
        </div>
        {info && (
          <small className={'vin-result ' + (info.valid === false || info.checkOk === false ? 'warn' : 'ok')}>
            {info.valid === false ? info.message
              : <>{[info.year, info.make, info.model].filter(Boolean).join(' ') || 'VIN looks right'}{info.engine ? ` · ${info.engine}` : ''}{info.gvwr ? ` · ${info.gvwr}` : ''}
                {info.checkOk === false && <b> · Check the VIN for a typo (check digit doesn't match)</b>}
                {info.message && <> · {info.message}</>}</>}
          </small>
        )}
        {others.map(e => (
          <small key={e.id} className="vin-result warn">Already saved: unit #{e.unit}{e.customer_name ? ` · ${e.customer_name}` : ''}{e.mileage ? ` · ${e.mileage.toLocaleString()} mi last visit` : ''}
            {onExisting && <> <button type="button" className="linkish" onClick={() => onExisting(e)}>{customerId && e.customer_id && e.customer_id !== customerId ? 'Move it to this customer' : 'Use this unit'}</button></>}</small>
        ))}
      </label>
      <Field label="Unit number" name={prefix + 'unit'} value={f.unit} onChange={set('unit')} required={unitRequired} placeholder="e.g. 214" />
      <label className="field"><span>Type *</span><select name={prefix + 'type'} value={f.type} onChange={set('type')} required>
        <option value="">Choose type…</option>
        {only !== 'trailer' && <optgroup label="Trucks">{TRUCK_TYPES.map(t => <option key={t}>{t}</option>)}</optgroup>}
        {only !== 'truck' && <optgroup label="Trailers">{TRAILER_TYPES.map(t => <option key={t}>{t}</option>)}</optgroup>}
        {only && f.type && (only === 'truck' ? TRAILER_TYPES : TRUCK_TYPES).includes(f.type) && <option>{f.type}</option>}
        <option>Other</option>
        {(f.type === 'Truck' || f.type === 'Trailer') && <option>{f.type}</option>}
      </select></label>
      <Field label="Year" name={prefix + 'year'} value={f.year} onChange={set('year')} maxLength={4} inputMode="numeric" />
      <Field label="Make" name={prefix + 'make'} value={f.make} onChange={set('make')} placeholder="Freightliner" />
      <Field label="Model" name={prefix + 'model'} value={f.model} onChange={set('model')} placeholder="Cascadia" />
      <Field label="Plate" name={prefix + 'plate'} value={f.plate} onChange={set('plate')} />
    </>
  );
}

/** Store picker: stores used before + common ones, and "+ New store" to type any other. */
export function StoreField({ name = 'vendor', label = 'Store', defaultValue = '', required, full = true }: { name?: string; label?: string; defaultValue?: string; required?: boolean; full?: boolean }) {
  const { data } = useApi<string[]>('/api/expenses/stores');
  const list = Array.from(new Set([...(data || []), ...(defaultValue ? [defaultValue] : [])]));
  const [isNew, setIsNew] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [typed, setTyped] = useState('');
  return (
    <label className={'field' + (full ? ' full' : '')}><span>{label}{required && ' *'}</span>
      {isNew ? <>
        <input name={name} value={typed} onChange={e => setTyped(e.target.value)} required={required} autoFocus placeholder="Type the store name" maxLength={160} />
        <small><button type="button" className="linkish" onClick={() => setIsNew(false)}>← Pick from the list</button></small>
      </> : (
        <select name={name} value={value} required={required} onChange={e => { if (e.target.value === '__new') { setIsNew(true); setTyped(''); } else setValue(e.target.value); }}>
          <option value="">Choose store…</option>
          {list.map(s => <option key={s} value={s}>{s}</option>)}
          <option value="__new">+ New store…</option>
        </select>
      )}
    </label>
  );
}

/** Receipt photo: take or pick one, shows a preview. Tap the preview to retake. */
export function ReceiptPicker({ file, onChange, current, required, label = 'Receipt photo', button = 'Take photo of receipt' }: { file: File | null; onChange: (f: File | null) => void; current?: string | null; required?: boolean; label?: string; button?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const gal = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  useEffect(() => { if (!file) { setUrl(''); return; } const u = URL.createObjectURL(file); setUrl(u); return () => URL.revokeObjectURL(u); }, [file]);
  const src = url || (current ? photoSrc(current) : '');
  return (
    <div className="field full receipt-pick">
      <span>{label}{required && ' *'}</span>
      <input ref={ref} type="file" accept="image/*" capture="environment" hidden onChange={e => onChange(e.target.files?.[0] || null)} />
      <input ref={gal} type="file" accept="image/*" hidden onChange={e => onChange(e.target.files?.[0] || null)} />
      {src ? <button type="button" className="receipt-preview" onClick={() => ref.current?.click()}><img src={src} alt="Receipt" /><small>Tap to retake</small></button>
        : <button type="button" className="btn secondary big full" onClick={() => ref.current?.click()}><Icon name="camera" size={20} />{button}</button>}
      <button type="button" className="linkish small" onClick={() => gal.current?.click()}>🖼 Choose from gallery</button>
    </div>
  );
}

/** Pick up to `max` photos: shows the ones already saved (× to remove) and new ones from the camera / files. */
export function PhotoSetPicker({ keep, onKeep, files, onFiles, max = 4, label = 'Photos' }: {
  keep: string[]; onKeep: (ids: string[]) => void; files: File[]; onFiles: (f: File[]) => void; max?: number; label?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const cam = useRef<HTMLInputElement>(null);
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => { const u = files.map(f => URL.createObjectURL(f)); setUrls(u); return () => u.forEach(URL.revokeObjectURL); }, [files]);
  const left = max - keep.length - files.length;
  const toast = useToast();
  const [broken, setBroken] = useState<Record<string, boolean>>({});
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const all: File[] = Array.from(e.target.files || []);
    e.target.value = '';
    // A Google Photos picture that is only in the cloud comes in empty.
    const empty = all.filter(f => !f.size);
    if (empty.length) toast('That photo is still in the cloud (Google Photos) — open it once in Gallery so it downloads, or use Take photo', 'err');
    const add = all.filter(f => f.size).slice(0, Math.max(0, left));
    if (add.length) onFiles([...files, ...add]);
  };
  const bad = (k: string) => () => setBroken(b => (b[k] ? b : { ...b, [k]: true }));
  const heicTip = 'This phone saves photos as HEIC, which browsers can’t show. Samsung: Camera → Settings → Advanced picture options → turn off “High efficiency pictures”. iPhone: Settings → Camera → Formats → Most Compatible.';
  return (
    <div className="field full photo-set">
      <span>{label} <small className="muted">({keep.length + files.length} of {max})</small></span>
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={pick} />
      <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={pick} />
      <div className="photo-set-row">
        {keep.map(id => <span key={id} className="ps-item">{broken[id] ? <small className="ps-bad" title={heicTip}>Can’t show this photo</small> : <img src={photoSrc(id)} alt="Part" onError={bad(id)} />}<button type="button" aria-label="Remove photo" onClick={() => onKeep(keep.filter(x => x !== id))}>×</button></span>)}
        {urls.map((u, i) => <span key={u} className="ps-item new">{broken[u] ? <small className="ps-bad" title={heicTip}>Can’t preview</small> : <img src={u} alt="New photo" onError={bad(u)} />}<button type="button" aria-label="Remove photo" onClick={() => onFiles(files.filter((_, j) => j !== i))}>×</button></span>)}
        {left > 0 && <>
          <button type="button" className="ps-add" onClick={() => cam.current?.click()}><Icon name="camera" size={22} /><small>Take photo</small></button>
          <button type="button" className="ps-add" onClick={() => ref.current?.click()}><Icon name="image" size={22} /><small>Gallery</small></button></>}
      </div>
      {Object.keys(broken).length > 0 && <small className="warn-text">{heicTip}</small>}
    </div>
  );
}

/** First photo as a thumbnail (+N when there are more); opens all of them big. */
export function PhotoThumbs({ ids }: { ids: string[] }) {
  const [open, setOpen] = useState(false);
  if (!ids.length) return null;
  return (
    <>
      <button type="button" className="receipt-thumb multi" title={`${ids.length} photo${ids.length > 1 ? 's' : ''}`} onClick={e => { e.stopPropagation(); setOpen(true); }}>
        <img src={photoSrc(ids[0])} alt="Part" loading="lazy" />{ids.length > 1 && <b>+{ids.length - 1}</b>}
      </button>
      {open && <div className="overlay lightbox gallery" onClick={() => setOpen(false)}><div className="gallery-row">{ids.map(id => <img key={id} src={photoSrc(id)} alt="Part" />)}</div><small className="lightbox-cap">Tap to close</small></div>}
    </>
  );
}

/** Small receipt thumbnail that opens full size. */
export function ReceiptThumb({ id }: { id?: string | null }) {
  const [open, setOpen] = useState(false);
  if (!id) return <span className="muted">—</span>;
  return (
    <>
      <button type="button" className="receipt-thumb" title="View receipt" onClick={e => { e.stopPropagation(); setOpen(true); }}><img src={photoSrc(id)} alt="Receipt" loading="lazy" /></button>
      {open && <div className="overlay lightbox" onClick={() => setOpen(false)}><img src={photoSrc(id)} alt="Receipt" /></div>}
    </>
  );
}

/** Big camera button. On phones `capture` opens the camera directly. */
export function CameraButton({ kind, workOrderId, note, onDone, label = 'Take photo', className = '', askNote }: {
  kind: string; workOrderId?: string; note?: string; onDone: () => void; label?: string; className?: string; askNote?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    let n = note;
    if (askNote) { const r = window.prompt(askNote, ''); if (r === null) { if (ref.current) ref.current.value = ''; return; } n = r; }
    setBusy(true);
    try {
      for (const f of Array.from(files)) await uploadPhoto(f, { kind, work_order_id: workOrderId, note: n });
      toast(files.length > 1 ? `${files.length} photos uploaded` : 'Photo uploaded');
      onDone();
    } catch (e) { toast((e as Error).message, 'err'); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  }
  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden onChange={e => void onFiles(e.target.files)} />
      <button type="button" className={'btn secondary ' + className} disabled={busy} onClick={() => ref.current?.click()}>
        <Icon name="camera" size={18} />{busy ? 'Uploading…' : label}
      </button>
    </>
  );
}

export function Tabs({ tabs, value, onChange }: { tabs: { id: string; label: ReactNode }[]; value: string; onChange: (id: string) => void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map(t => <button key={t.id} role="tab" aria-selected={value === t.id} className={value === t.id ? 'on' : ''} onClick={() => onChange(t.id)}>{t.label}</button>)}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return <label className="search"><Icon name="search" size={16} /><input value={value} onChange={e => onChange(cleanPasted(e.target.value))} placeholder={placeholder} aria-label={placeholder} /></label>;
}

/** Debounced value, for search boxes that hit the API. */
export function useDebounced<T>(value: T, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// The preview page can't show browser dialogs, so it skips the confirmation there.
export function confirmAction(msg: string) { return DEMO ? true : window.confirm(msg); }

/**
 * Saves what you type by itself: shortly after you stop typing, and right away if you leave the page.
 * Returns a status for a small "Saving… / Saved" note.
 */
export function useAutosave<T>(value: T, initial: T, save: (v: T) => Promise<unknown>, delay = 900) {
  const last = useRef(JSON.stringify(initial));
  const latest = useRef(value);
  const saver = useRef(save);
  saver.current = save; latest.current = value;
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const flush = useRef(async () => {
    const json = JSON.stringify(latest.current);
    if (json === last.current) return;
    last.current = json;
    setStatus('saving');
    try { await saver.current(latest.current); setStatus('saved'); } catch { last.current = ''; setStatus('error'); }
  });
  useEffect(() => {
    if (JSON.stringify(value) === last.current) return;
    const t = setTimeout(() => void flush.current(), delay);
    return () => clearTimeout(t);
  }, [JSON.stringify(value)]);
  useEffect(() => () => { void flush.current(); }, []);
  return status;
}

export function SaveNote({ status }: { status: string }) {
  if (status === 'idle') return null;
  return <small className={'save-note ' + status}>{status === 'saving' ? 'Saving…' : status === 'saved' ? '✓ Saved' : 'Not saved — check your connection'}</small>;
}

/** Barcode / UPC scanner with the phone camera. Chrome / Android use the browser's own detector; iPhone (Safari) loads a
 *  scanning library on first use. If neither works, "Read from a photo" lets AI read the number under the barcode. */
type Detector = { detect: (src: HTMLVideoElement) => Promise<{ rawValue: string }[]> };
export const canScan = () => typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
const nativeScan = () => typeof window !== 'undefined' && 'BarcodeDetector' in window;
type H5 = { start: (cam: unknown, cfg: unknown, ok: (t: string) => void, bad: () => void) => Promise<void>; stop: () => Promise<void> };
let h5Loading: Promise<new (id: string, o?: unknown) => H5> | null = null;
/** html5-qrcode (pinned version) from the CDN, once per page. */
function loadH5(): Promise<new (id: string, o?: unknown) => H5> {
  const g = window as unknown as { Html5Qrcode?: new (id: string, o?: unknown) => H5 };
  if (g.Html5Qrcode) return Promise.resolve(g.Html5Qrcode);
  h5Loading = h5Loading || new Promise((ok, no) => {
    const sc = document.createElement('script');
    sc.src = 'https://cdn.jsdelivr.net/npm/html5-qrcode@2.3.8/html5-qrcode.min.js';
    const t = setTimeout(() => no(new Error('Scanner did not load — check the internet, or read it from a photo')), 12000);
    sc.onload = () => { clearTimeout(t); g.Html5Qrcode ? ok(g.Html5Qrcode) : no(new Error('Scanner did not load')); };
    sc.onerror = () => { clearTimeout(t); h5Loading = null; no(new Error('Scanner did not load — check the internet, or read it from a photo')); };
    document.head.appendChild(sc);
  });
  return h5Loading;
}
export function ScanButton({ onCode, label = 'Scan', photoKind = 'part' }: { onCode: (code: string) => void; label?: string; photoKind?: 'part' | 'vin' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn secondary scan-btn" title="Scan a barcode with the camera" onClick={() => setOpen(true)}>
        <Icon name="camera" size={16} />{label}</button>
      {open && <ScanModal photoKind={photoKind} onClose={() => setOpen(false)} onCode={c => { setOpen(false); onCode(c); }} />}
    </>
  );
}
function ScanModal({ onClose, onCode, photoKind = 'part' }: { onClose: () => void; onCode: (c: string) => void; photoKind?: 'part' | 'vin' }) {
  return (
    <Modal title={photoKind === 'vin' ? 'Scan VIN' : 'Scan barcode'} onClose={onClose}>
      <div className="scan-box">
        <LiveScanner onCode={onCode} photoKind={photoKind} />
      </div>
    </Modal>
  );
}

/**
 * Live camera scanner (barcodes on part boxes, the VIN barcode on the door frame).
 * Android Chrome: the phone's own detector — sharp camera (1080p), continuous focus, a little zoom, torch button.
 * If it hasn't read anything in 5 s (or the phone has no detector, e.g. iPhone) it switches to the scanning library.
 */
export function LiveScanner({ onCode, hint = 'Hold the barcode inside the frame, 10–20 cm away.', photoKind = 'part' }: { onCode: (code: string) => void; hint?: string; photoKind?: 'part' | 'vin' }) {
  // iPhone / iPad have no built-in barcode reader for web apps (and the live camera often stays white in a home-screen app):
  // start with "Take photo" there; the live camera is one tap away.
  const ios = typeof navigator !== 'undefined' && (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  // In Safari / Chrome on an iPhone or iPad the live camera works (scanned by the library) — like on Samsung.
  // Only the home-screen app (where iOS often shows no camera picture) starts with "Take photo".
  const standalone = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true);
  const [photoFirst, setPhotoFirst] = useState(ios && standalone && !nativeScan());
  if (photoFirst) return (
    <div className="live-scan">
      <PhotoRead kind={photoKind} label={photoKind === 'vin' ? 'Take photo of the VIN' : 'Take photo of the barcode / part #'} className="btn primary big" onResult={r => { const v = String((photoKind === 'vin' ? r.vin : r.code) || ''); if (v) onCode(v); }} />
      <p className="muted small">The number is read from the picture. <button type="button" className="linkish" onClick={() => setPhotoFirst(false)}>Try the live camera instead</button></p>
    </div>
  );
  return <LiveCamera onCode={onCode} hint={hint} photoKind={photoKind} />;
}

function LiveCamera({ onCode, hint, photoKind }: { onCode: (code: string) => void; hint: string; photoKind: 'part' | 'vin' }) {
  const video = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState(canScan() ? '' : 'This phone or browser can\'t open the camera here — read the number from a photo instead.');
  const [lib, setLib] = useState(!nativeScan());
  const [torch, setTorch] = useState<null | boolean>(null);
  const [stuck, setStuck] = useState(false);
  const track = useRef<MediaStreamTrack | null>(null);
  const boxId = useRef('scan-' + Math.random().toString(36).slice(2));
  const done = useRef(false);
  const hit = (t: string) => { if (done.current || !t) return; done.current = true; try { navigator.vibrate?.(60); } catch { /* */ } onCode(t.trim()); };
  useEffect(() => {
    if (!canScan()) return;
    let stream: MediaStream | null = null, stop = false, h5: H5 | null = null, fallback: ReturnType<typeof setTimeout> | null = null;
    (async () => {
      try {
        if (lib) {
          const H5Q = await loadH5();
          if (stop) return;
          h5 = new H5Q(boxId.current, { verbose: false, experimentalFeatures: { useBarCodeDetectorIfSupported: false } });
          await h5.start({ facingMode: 'environment' }, { fps: 12, aspectRatio: 1.333, videoConstraints: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
            qrbox: (w: number, h: number) => ({ width: Math.round(w * .9), height: Math.round(Math.min(h * .45, 260)) }) },
            t => { if (!stop && t) { stop = true; h5?.stop().catch(() => {}); hit(t); } }, () => {});
          // Some phones (older iPhones, home-screen apps) open the camera but show no picture: offer the photo way.
          fallback = setTimeout(() => { const v = document.querySelector<HTMLVideoElement>(`#${boxId.current} video`); if (!stop && !done.current && (!v || v.readyState < 2 || !v.videoWidth)) setStuck(true); }, 4000);
          return;
        }
        const BD = (window as unknown as { BarcodeDetector: (new (o?: unknown) => Detector) & { getSupportedFormats?: () => Promise<string[]> } }).BarcodeDetector;
        const want = ['code_128', 'code_39', 'code_93', 'upc_a', 'upc_e', 'ean_13', 'ean_8', 'itf', 'codabar', 'qr_code', 'data_matrix', 'pdf417'];
        const have = await BD.getSupportedFormats?.().catch(() => want) || want;
        const det = new BD({ formats: want.filter(f => have.includes(f)) });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } } });
        if (!video.current || stop) { stream.getTracks().forEach(t => t.stop()); return; }
        const tr = stream.getVideoTracks()[0]; track.current = tr;
        // Sharp picture: keep focusing, zoom in a little for small barcodes, offer the torch.
        try {
          const caps = (tr.getCapabilities?.() || {}) as { focusMode?: string[]; zoom?: { min: number; max: number }; torch?: boolean };
          const adv: Record<string, unknown>[] = [];
          if (caps.focusMode?.includes('continuous')) adv.push({ focusMode: 'continuous' });
          if (caps.zoom && caps.zoom.max >= 1.5) adv.push({ zoom: Math.min(caps.zoom.max, 1.6) });
          if (adv.length) await tr.applyConstraints({ advanced: adv } as MediaTrackConstraints).catch(() => {});
          if (caps.torch) setTorch(false);
        } catch { /* camera without these settings */ }
        video.current.srcObject = stream;
        await video.current.play();
        fallback = setTimeout(() => { if (!stop && !done.current) setLib(true); }, 5000);
        while (!stop) {
          const found = await det.detect(video.current).catch(() => []);
          if (found[0]?.rawValue) { hit(found[0].rawValue); return; }
          await new Promise(r => setTimeout(r, 120));
        }
      } catch (e) {
        const m = (e as Error)?.message || String(e || '');
        setErr(/permission|denied|notallowed/i.test(m) ? 'Camera is blocked for this site. Allow it in the phone settings (Chrome: ⋮ → Settings → Site settings → Camera; iPhone: Settings → Safari → Camera), or read the number from a photo.' : m || 'Camera not available');
      }
    })();
    return () => { stop = true; if (fallback) clearTimeout(fallback); stream?.getTracks().forEach(t => t.stop()); track.current = null; h5?.stop().catch(() => {}); };
  }, [lib]);
  const flip = async () => { const tr = track.current; if (!tr || torch === null) return; try { await tr.applyConstraints({ advanced: [{ torch: !torch }] } as unknown as MediaTrackConstraints); setTorch(!torch); } catch { setTorch(null); } };
  return (
    <div className="live-scan">
      {err ? <div className="error-box"><Icon name="alert" />{err}</div>
        : lib ? <div id={boxId.current} key="lib" className="scan-lib" />
        : <div className="scan-cam" key="cam"><video ref={video} playsInline muted /><i className="scan-frame" aria-hidden="true" />
            {torch !== null && <button type="button" className="scan-torch" onClick={() => void flip()}>{torch ? '🔦 Light off' : '🔦 Light'}</button>}</div>}
      {stuck && !err && <div className="error-box"><Icon name="alert" />The camera picture didn't start on this phone. Use “Take photo” below — the number is read from the picture.</div>}
      {!err && <p className="muted small">{hint}{!lib && <> <button type="button" className="linkish" onClick={() => setLib(true)}>Not reading? Try the other scanner</button></>}</p>}
      <PhotoRead kind={photoKind} label={err || stuck ? 'Take photo — read the number' : 'Or take a photo — AI reads the number'} className={err || stuck ? 'btn primary' : 'btn secondary'} onResult={r => { const v = String((photoKind === 'vin' ? r.vin : r.code) || ''); if (v) hit(v); }} />
    </div>
  );
}

/** Take (or pick) a photo and let AI read it: a VIN, a company name + USDOT, or a part number. The result only fills the form. */
export function PhotoRead({ kind, label, onResult, className = 'btn secondary', small }: { kind: 'vin' | 'company' | 'part'; label?: string; onResult: (r: Row) => void; className?: string; small?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  const gal = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const text = label ?? (kind === 'vin' ? 'VIN from photo' : kind === 'company' ? 'From truck door photo' : 'Read from photo');
  const read = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const r = await readPhoto(kind, f);
      if (!r.ok && r.message) toast(String(r.message), 'err');
      else if (!r.ok) toast('Couldn\'t read it — try a closer, straight photo without glare', 'err');
      onResult(r);
    } catch (er) { toast((er as Error).message, 'err'); } finally { setBusy(false); }
  };
  return (
    <>
      {/* Camera opens straight away; "Gallery" picks a photo already taken. */}
      <input ref={ref} type="file" accept="image/*" capture="environment" hidden onChange={read} />
      <input ref={gal} type="file" accept="image/*" hidden onChange={read} />
      <span className="photo-read-pair">
        <button type="button" className={className + (small ? ' small' : '') + ' photo-read'} disabled={busy} onClick={() => ref.current?.click()} title="Take a photo — AI reads it">
          <Icon name="camera" size={16} />{busy ? 'Reading…' : text}</button>
        <button type="button" className={'btn ghost' + (small ? ' small' : '') + ' gallery-btn'} disabled={busy} onClick={() => gal.current?.click()} title="Choose a photo from the gallery">🖼 Gallery</button>
      </span>
    </>
  );
}

/** Payment method + its reference: "Check #" for checks, "Confirmation #" for ACH / Zelle / card, nothing for cash. */
export function PayMethodFields({ methods, label = 'Method', blank }: { methods: string[]; label?: string; blank?: boolean }) {
  const [m, setM] = useState(blank ? '' : methods[0]);
  const ref = /check/i.test(m) ? 'Check #' : /cash/i.test(m) || !m ? '' : /card/i.test(m) ? 'Last 4 digits / confirmation # (optional)' : 'Confirmation # (optional)';
  return (<>
    <Select label={label} name="method" value={m} onChange={e => setM(e.target.value)}>{blank && <option value="">—</option>}{methods.map(x => <option key={x}>{x}</option>)}</Select>
    {ref ? <Field key={ref} label={ref} name="reference" /> : <input type="hidden" name="reference" value="" />}
  </>);
}

/** 1226000 → "1,226,000" while typing. */
export const withCommas = (raw: string) => { const d = String(raw || '').replace(/\D/g, '').replace(/^0+(?=\d)/, ''); return d ? Number(d).toLocaleString('en-US') : ''; };
/** A mileage that looks like a typo: lower than last time, or a jump nobody drives between visits. */
export function mileageWarning(n: number, prev?: number) {
  if (!n) return '';
  if (prev && n < prev) return `Lower than last visit (${prev.toLocaleString('en-US')} mi) — check the dash`;
  if (prev && n - prev > 150000) return `+${(n - prev).toLocaleString('en-US')} mi since last visit (${prev.toLocaleString('en-US')}) — check for an extra digit`;
  if (n > 3_000_000) return 'Over 3 million miles — check for an extra digit';
  return '';
}
/** Mileage box for forms: shows commas, sends the plain number. */
export function MileageField({ label = 'Mileage', name = 'mileage', defaultValue, required, hint, prev, full }: { label?: string; name?: string; defaultValue?: unknown; required?: boolean; hint?: ReactNode; prev?: number; full?: boolean }) {
  const [val, setVal] = useState(withCommas(String(defaultValue || '')));
  const n = Number(val.replace(/\D/g, '') || 0);
  const warn = mileageWarning(n, prev);
  return (
    <label className={'field' + (full ? ' full' : '')}><span>{label}{required && ' *'}</span>
      <input inputMode="numeric" autoComplete="off" value={val} required={required} placeholder="e.g. 1,226,000" onChange={e => setVal(withCommas(e.target.value))} />
      <input type="hidden" name={name} value={n || ''} />
      {warn ? <small className="warn-text">⚠ {warn}</small> : hint && <small>{hint}</small>}
    </label>
  );
}

const CHIP_COLORS = ['#2563eb', '#9333ea', '#db2777', '#ea580c', '#0d9488', '#4f46e5', '#b45309', '#0891b2', '#be123c', '#4d7c0f'];
const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase() || '?';
/** Who's on a job: colored initials + name ("You" in green, "Free" in grey), with the job's state. Same color for a person everywhere. */
export function TechChip({ userId, name, meId, status, running, short }: { userId?: string | null; name?: string | null; meId?: string; status?: string; running?: boolean; short?: boolean }) {
  const mine = !!userId && userId === meId;
  const color = !userId ? '#94a3b8' : mine ? '#16a34a' : CHIP_COLORS[[...userId].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) >>> 0, 7) % CHIP_COLORS.length];
  const st = status === 'done' ? '✓ Done' : running || status === 'doing' ? '⏱ Working' : status === 'waiting' ? '⏸ Waiting parts' : '';
  return (
    <span className={'tech-chip' + (mine ? ' mine' : '') + (!userId ? ' free' : '')} style={{ ['--chip' as string]: color }}>
      <i>{userId ? initials(name || '?') : '–'}</i>{short ? null : <b>{!userId ? 'Free' : mine ? 'You' : name}</b>}{st && <em className={'st-' + (status || '')}>{st}</em>}
    </span>
  );
}

/** Pick one from a long list by typing (company by name or USDOT, truck by unit # or VIN). "+ Add new" is always first;
 *  when nothing matches it offers to add exactly what you typed. The list floats over everything (never cut off) and scrolls. */
export function SearchPick({ label, items, value, onChange, onAdd, addLabel, placeholder, required, full = true, hint }: {
  label: string; items: { id: string; label: string; sub?: string; search?: string }[]; value: string; onChange: (id: string) => void;
  onAdd?: (typed: string) => void; addLabel?: string; placeholder?: string; required?: boolean; full?: boolean; hint?: ReactNode;
}) {
  const sel = items.find(i => i.id === value);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; width: number; maxHeight: number } | null>(null);
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const digits = q.replace(/\D/g, '');
  const list = (q ? items.filter(i => { const t = `${i.label} ${i.sub || ''} ${i.search || ''}`.toLowerCase(); return words.every(w => t.includes(w)) || (digits.length >= 3 && t.replace(/\D/g, ' ').includes(digits)); }) : items).slice(0, 80);
  const rows: { kind: 'add' | 'item'; id?: string; label: string; sub?: string }[] = [
    ...(onAdd ? [{ kind: 'add' as const, label: q.trim() && !list.length ? `+ Add "${q.trim()}" as new` : addLabel || '+ Add new' }] : []),
    ...list.map(i => ({ kind: 'item' as const, id: i.id, label: i.label, sub: i.sub })),
  ];
  useEffect(() => setHi(onAdd && !q.trim() ? 0 : onAdd && list.length ? 1 : 0), [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = ref.current?.getBoundingClientRect(); if (!r) return;
      const below = window.innerHeight - r.bottom - 10, above = r.top - 10;
      const width = Math.min(Math.max(r.width, 320), window.innerWidth - 16), left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setPos(below >= 220 || below >= above ? { left, width, top: r.bottom + 4, maxHeight: Math.min(360, below) } : { left, width, bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(360, above) });
    };
    place(); window.addEventListener('scroll', place, true); window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open]);
  const choose = (r: typeof rows[number]) => { setOpen(false); setQ(''); if (r.kind === 'add') onAdd?.(q.trim()); else onChange(r.id!); };
  return (
    <label className={'field' + (full ? ' full' : '')}><span>{label}{required && ' *'}</span>
      <input ref={ref} value={open ? q : sel ? sel.label + (sel.sub ? ` · ${sel.sub}` : '') : ''} placeholder={sel ? undefined : placeholder || 'Type to search…'} autoComplete="off" required={required && !value}
        onFocus={() => { setOpen(true); setQ(''); }} onBlur={() => setTimeout(() => setOpen(false), 150)} onChange={e => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(rows.length - 1, h + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
          else if (e.key === 'Enter' && open && rows[hi]) { e.preventDefault(); choose(rows[hi]); }
          else if (e.key === 'Escape') setOpen(false);
        }} />
      {hint && <small>{hint}</small>}
      {open && pos && createPortal(
        <div className="pick-pop" style={pos} onMouseDown={e => e.preventDefault()} role="listbox">
          {q && <div className="ps-head">{list.length ? `${list.length}${list.length === 80 ? '+' : ''} found` : 'Nothing found'}</div>}
          {rows.map((r, i) => <button key={r.id || 'add'} type="button" className={(i === hi ? 'on ' : '') + (r.kind === 'add' ? 'pick-add' : '')} onMouseEnter={() => setHi(i)} onMouseDown={e => { e.preventDefault(); choose(r); }}>
            <b>{r.label}</b>{r.sub && <small>{r.sub}</small>}</button>)}
        </div>, document.body)}
    </label>
  );
}

/** Company from a truck-door photo or a typed USDOT: FMCSA's legal name, address and phone fill the form's empty fields;
 *  a customer that already has this USDOT is offered instead of making a second one. */
export function CompanyLookup({ onUse }: { onUse?: (id: string, name: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [res, setRes] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const field = (n: string) => box.current?.closest('form')?.querySelector<HTMLInputElement>(`[name="${n}"]`) || null;
  const fill = (n: string, val: unknown, force = false) => { const el = field(n); if (el && val && (force || !el.value.trim())) el.value = String(val); };
  function apply(r: Row) {
    const ca = r.carrier as Row | null;
    if (r.kind === 'company' && r.usdot && !ca) toast(r.fmcsa === false ? `Read USDOT ${r.usdot} — FMCSA lookup is not turned on (FMCSA key)` : r.fmcsa_error || `USDOT ${r.usdot} not found at FMCSA`, 'err');
    if (r.usdot) fill('usdot', r.usdot, true);
    fill('name', ca?.legal_name || r.name);
    if (ca) {
      fill('phone', ca.phone); fill('mc', ca.mc);
      fill('address', [ca.address, ca.city, [ca.state, ca.zip].filter(Boolean).join(' ')].filter(Boolean).join(', '));
    }
    setRes(r);
  }
  async function lookUp() {
    const dot = (field('usdot')?.value || '').replace(/\D/g, '');
    if (!dot) { toast('Type the USDOT number first', 'err'); return; }
    setBusy(true);
    try {
      const r = await api<Row>(`/api/fmcsa/${dot}`);
      if (r.setup === false) toast('USDOT lookup is not turned on yet (FMCSA key)', 'err');
      else if (!r.carrier) toast(r.error || 'USDOT not found at FMCSA — check the number', 'err');
      apply({ ...r, usdot: dot });
    } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
  }
  const ca = res?.carrier as Row | null | undefined;
  const matches = (res?.matches || []) as Row[];
  return (
    <div className="full" ref={box}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <PhotoRead kind="company" small onResult={apply} />
        <button type="button" className="btn secondary small" disabled={busy} onClick={() => void lookUp()}>{busy ? 'Looking up…' : 'Look up USDOT'}</button>
      </div>
      {ca && <div className="fmcsa-card" style={{ marginTop: 8 }}><b>{ca.legal_name}{ca.dba_name ? ` (DBA ${ca.dba_name})` : ''}</b>
        {[ca.address, ca.city, ca.state, ca.zip].filter(Boolean).join(', ')}{ca.phone ? ` · ${ca.phone}` : ''} · USDOT {ca.usdot}{ca.mc ? ` · MC ${ca.mc}` : ''}
        {String(ca.allowed).toUpperCase() === 'N' && <div className="danger">FMCSA: not allowed to operate</div>}
        <small className="muted" style={{ display: 'block' }}>From FMCSA — filled into the empty fields. Check before saving.</small></div>}
      {matches.map(x => (
        <div key={String(x.id)} className="note-bar warn" style={{ marginTop: 8 }}><span className="grow">Already a customer: <b>{x.name}</b>{x.usdot ? ` · USDOT ${x.usdot}` : ''}{x.match === 'name' ? ' (similar name)' : ''}</span>
          {onUse && <button type="button" className="btn secondary small" onClick={() => onUse(String(x.id), String(x.name))}>Use this one</button>}</div>
      ))}
    </div>
  );
}

/** Quantity box for phones: tapping selects the number (type 9 → 9, not 19), it may be empty while typing, and it settles to at least 1. */
export function QtyInput({ value, onChange, label = 'Quantity' }: { value: number; onChange: (n: number) => void; label?: string }) {
  const [text, setText] = useState(String(value));
  useEffect(() => { if (Number(text) !== value) setText(String(value)); }, [value]);
  return <input type="text" inputMode="numeric" pattern="[0-9]*" value={text} aria-label={label} className="qty-input"
    onFocus={e => { const el = e.target; setTimeout(() => el.select(), 0); }}
    onChange={e => { const t = e.target.value.replace(/\D/g, '').slice(0, 5); setText(t); if (Number(t) >= 1) onChange(Number(t)); }}
    onBlur={() => { const n = Math.max(1, Number(text) || 1); setText(String(n)); onChange(n); }} />;
}

/**
 * A drop-in for a long <select> (vendors, customers…): tap it → a list with a search box on top and
 * "+ Add …" right under it (not lost at the bottom of 100 names). Works the same on Android, iPhone and desktop.
 */
export function PickSelect({ value, options, onChange, none, onAdd, addLabel = '+ Add new…', ariaLabel, className = '', disabled, placeholder }: {
  value: string; options: { value: string; label: string; sub?: string }[]; onChange: (v: string) => void;
  none?: string; onAdd?: (typed: string) => void; addLabel?: string; ariaLabel?: string; className?: string; disabled?: boolean; placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const btn = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; width: number; maxHeight: number } | null>(null);
  const sel = options.find(o => o.value === value);
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const list = (q ? options.filter(o => { const t = `${o.label} ${o.sub || ''}`.toLowerCase(); return words.every(w => t.includes(w)); }) : options).slice(0, 150);
  const exact = options.some(o => o.label.toLowerCase() === q.trim().toLowerCase());
  const rows: { kind: 'add' | 'none' | 'item'; value: string; label: string; sub?: string }[] = [
    ...(onAdd ? [{ kind: 'add' as const, value: '', label: q.trim() && !exact ? `+ Add "${q.trim()}"` : addLabel }] : []),
    ...(none !== undefined && !q ? [{ kind: 'none' as const, value: '', label: none }] : []),
    ...list.map(o => ({ kind: 'item' as const, value: o.value, label: o.label, sub: o.sub })),
  ];
  useEffect(() => setHi(q && list.length ? (onAdd ? 1 : 0) : 0), [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btn.current?.getBoundingClientRect(); if (!r) return;
      const phone = window.innerWidth < 600;
      const width = phone ? window.innerWidth - 16 : Math.min(Math.max(r.width, 300), window.innerWidth - 16);
      const left = phone ? 8 : Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const below = window.innerHeight - r.bottom - 10, above = r.top - 10;
      // Phone: at the top of the screen, so the keyboard doesn't cover the list.
      setPos(phone ? { left, width, top: 12, maxHeight: Math.round(window.innerHeight * 0.55) }
        : below >= 260 || below >= above ? { left, width, top: r.bottom + 4, maxHeight: Math.min(400, below) } : { left, width, bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(400, above) });
    };
    place(); window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);
  const close = () => { setOpen(false); setQ(''); };
  const choose = (r: typeof rows[number]) => { close(); if (r.kind === 'add') onAdd?.(q.trim()); else onChange(r.value); };
  return (
    <>
      <button ref={btn} type="button" className={'pick-select ' + className} disabled={disabled} aria-label={ariaLabel} aria-haspopup="listbox" onClick={() => setOpen(true)}>
        <span className={sel || value ? '' : 'muted'}>{sel ? sel.label : value || (none ?? placeholder ?? 'Choose…')}</span><i aria-hidden="true">▾</i></button>
      {open && pos && createPortal(
        <div className="pick-dim" onMouseDown={close} onTouchStart={e => { if (e.target === e.currentTarget) close(); }}>
          <div className="pick-pop pick-select-pop" style={pos} onMouseDown={e => e.stopPropagation()} role="listbox" aria-label={ariaLabel}>
            <input className="pick-search" autoFocus value={q} placeholder="Search…" aria-label="Search" onChange={e => setQ(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(rows.length - 1, h + 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
                else if (e.key === 'Enter' && rows[hi]) { e.preventDefault(); choose(rows[hi]); }
                else if (e.key === 'Escape') close();
              }} />
            {q && !list.length && <div className="ps-head">Nothing found</div>}
            {rows.map((r, i) => <button key={r.kind + r.value + i} type="button" className={(i === hi ? 'on ' : '') + (r.kind === 'add' ? 'pick-add' : '') + (r.value === value && r.kind !== 'add' ? ' picked' : '')}
              onMouseEnter={() => setHi(i)} onClick={() => choose(r)}><b>{r.label}</b>{r.sub && <small>{r.sub}</small>}</button>)}
          </div>
        </div>, document.body)}
    </>
  );
}

/** Vendor field inside a form: searchable list of the shop's vendors; a name not in the list can be typed (it becomes a vendor). */
export function VendorPick({ name, defaultValue = '', required, value, onChange }: { name?: string; defaultValue?: string; required?: boolean; value?: string; onChange?: (v: string) => void }) {
  const { data } = useApi<Row[]>('/api/vendors');
  const [own, setOwn] = useState(defaultValue);
  const v = value ?? own;
  const set = (x: string) => { setOwn(x); onChange?.(x); };
  const names = [...new Set([...(data || []).filter(x => x.active !== 0).map(x => String(x.name)), ...(v ? [v] : [])])].sort((a, b) => a.localeCompare(b));
  return (
    <>
      <PickSelect value={v} ariaLabel="Vendor" placeholder="Choose vendor…" options={names.map(n => ({ value: n, label: n }))} onChange={set}
        onAdd={t => { const n = t || prompt('Vendor name')?.trim(); if (n) set(n); }} addLabel="+ Type a new vendor…" />
      {name && <input type="text" name={name} value={v} required={required} onChange={() => undefined} tabIndex={-1} aria-hidden="true" className="sr-hidden-input" />}
    </>
  );
}
